import React from 'react';
import styled from 'styled-components';

const TableContainer = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  margin-top: 1rem;
  max-width: 100%;
  width: 100%;
  max-height: 400px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);

  /* Critical: Prevent table from affecting parent layout */
  width: 100%;
  box-sizing: border-box;

  /* Force container to respect parent boundaries */
  min-width: 0;
  flex-shrink: 0;

  /* Ensure table doesn't break out of container */
  position: relative;

  /* Ensure proper scrolling behavior */
  display: block;

  /* Add subtle scrollbar styling for both directions */
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  /* Responsive design for smaller screens */
  @media (max-width: 768px) {
    margin-top: 0.5rem;
    max-height: 300px;

    &::-webkit-scrollbar {
      height: 6px;
      width: 6px;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
  font-size: 0.9rem;
  white-space: nowrap;
  table-layout: auto;

  @media (max-width: 768px) {
    min-width: 500px;
    font-size: 0.8rem;
  }
`;

const Th = styled.th`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
  min-width: 120px;
  position: sticky;
  top: 0;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 0.5rem;
    min-width: 100px;
    font-size: 0.8rem;
  }
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  min-width: 120px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0.5rem;
    min-width: 100px;
  }
`;

const TableWrapper = styled.div`
  margin-top: 1rem;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  min-width: 0;
  flex-shrink: 0;
  position: relative;
`;

const TableTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: white;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TableDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.6);
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.3);
`;

interface TableChartRendererProps {
  title: string;
  description?: string;
  data: any[];
  loading?: boolean;
  error?: string;
}

const TableChartRenderer: React.FC<TableChartRendererProps> = ({
  title,
  description,
  data,
  loading = false,
  error
}) => {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const handleWheel = (e: React.WheelEvent) => {
    if (tableContainerRef.current) {
      e.preventDefault();
      tableContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  if (loading) {
    return (
      <div>
        <TableTitle>{title}</TableTitle>
        {description && <TableDescription>{description}</TableDescription>}
        <LoadingSpinner>
          <div>Loading table data...</div>
        </LoadingSpinner>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <TableTitle>{title}</TableTitle>
        {description && <TableDescription>{description}</TableDescription>}
        <ErrorMessage>❌ Error loading table: {error}</ErrorMessage>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div>
        <TableTitle>{title}</TableTitle>
        {description && <TableDescription>{description}</TableDescription>}
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center', 
          color: 'rgba(255, 255, 255, 0.6)',
          fontStyle: 'italic'
        }}>
          No data available for table
        </div>
      </div>
    );
  }

  const columns = Object.keys(data[0] || {});

  return (
    <div>
      <TableTitle>{title}</TableTitle>
      {description && <TableDescription>{description}</TableDescription>}
      
      <TableWrapper>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5rem",
          }}
        >
          <span
            style={{
              fontSize: "0.8rem",
              color: "rgba(255, 255, 255, 0.6)",
              fontStyle: "italic",
            }}
          >
            ↔ Scroll horizontally • ↕ Scroll vertically
          </span>
        </div>
        <TableContainer ref={tableContainerRef} onWheel={handleWheel}>
          <Table>
            <thead>
              <tr>
                {columns.map((column) => (
                  <Th key={column} title={column}>
                    {column}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 10).map((row, index) => (
                <tr key={index}>
                  {columns.map((column, cellIndex) => (
                    <Td key={cellIndex} title={String(row[column])}>
                      {String(row[column])}
                    </Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
        {data.length > 10 && (
          <div style={{
            fontSize: "0.8rem",
            color: "rgba(255, 255, 255, 0.6)",
            textAlign: "center",
            marginTop: "0.5rem",
            fontStyle: "italic"
          }}>
            Showing first 10 rows of {data.length} total rows
          </div>
        )}
      </TableWrapper>
    </div>
  );
};

export default TableChartRenderer;
